// ─── Admin User Search ───────────────────────────────────────────────────────

export interface AdminSearchUserRow {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  tier: string;
  koinBalance: number;
  lifetimeKoins: number;
  isActive: boolean;
  lastSeenAt?: string;
  avatar?: string;
  organizationNames: string[];
}

export interface AdminSearchUsersResult {
  users: AdminSearchUserRow[];
  totalCount: number;
}

// ─── Admin User Detail ───────────────────────────────────────────────────────

export interface AdminUserOrganization {
  organizationId: string;
  organizationName: string;
  role: string;
  joinedAt: string;
  isActive: boolean;
}

export interface AdminUserDetailUser {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  tier: string;
  koinBalance: number;
  lifetimeKoins: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string;
  lastSeenAt?: string;
  avatar?: string;
  onboardingStep: number;
  currentStreakDays: number;
  streakFreezesAvailable: number;
  accountStatus: string;
}

export interface AdminUserDetail {
  user: AdminUserDetailUser;
  organizations: AdminUserOrganization[];
  totalLedgerEntries: number;
  accountStatus: string;
}

// ─── Admin User Ledger ───────────────────────────────────────────────────────

export type KoinsTransactionKind = "EARN" | "SPEND" | "ADJUSTMENT" | "REVERSAL";

export type KoinsSourceType =
  | "PHYSICAL_ACTIVITY"
  | "TELEMETRY_STEPS"
  | "CHALLENGE_CREATE"
  | "EVENT_ATTEND"
  | "STREAK_3_DAY"
  | "STREAK_7_DAY"
  | "ADJUSTMENT"
  | "MARKETPLACE"
  | "ONBOARDING_BONUS"
  | "ORGANIZATION_BONUS"
  | "FIRST_PRIZE_BONUS"
  | "CHALLENGE_BUY_IN"
  | "CHALLENGE_PRIZE"
  | "CHALLENGE_REFUND"
  | "CHALLENGE_BURN";

export interface KoinsLedgerEntry {
  _id: string;
  userId: string;
  kind: KoinsTransactionKind;
  amount: number;
  sourceType: KoinsSourceType;
  sourceId?: string;
  createdAt: string;
  effectiveAt: string;
  idempotencyKey: string;
  reversalOfLedgerEntryId?: string;
  meta?: string;
  organizationId?: string;
}

export interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor?: string;
  endCursor?: string;
}

export interface AdminUserLedgerPage {
  edges: KoinsLedgerEntry[];
  pageInfo: PageInfo;
  totalCount: number;
}

// ─── Source Type Display Config ──────────────────────────────────────────────

export const SOURCE_TYPE_CONFIG: Record<
  KoinsSourceType,
  { labelKey: string; emoji: string; color: string }
> = {
  PHYSICAL_ACTIVITY: { labelKey: "users.sourceType.PHYSICAL_ACTIVITY", emoji: "🏃", color: "text-blue-500" },
  TELEMETRY_STEPS: { labelKey: "users.sourceType.TELEMETRY_STEPS", emoji: "👟", color: "text-green-500" },
  CHALLENGE_CREATE: { labelKey: "users.sourceType.CHALLENGE_CREATE", emoji: "🏆", color: "text-yellow-600" },
  EVENT_ATTEND: { labelKey: "users.sourceType.EVENT_ATTEND", emoji: "📅", color: "text-purple-500" },
  STREAK_3_DAY: { labelKey: "users.sourceType.STREAK_3_DAY", emoji: "🔥", color: "text-orange-500" },
  STREAK_7_DAY: { labelKey: "users.sourceType.STREAK_7_DAY", emoji: "🔥", color: "text-orange-600" },
  ADJUSTMENT: { labelKey: "users.sourceType.ADJUSTMENT", emoji: "🔧", color: "text-gray-500" },
  MARKETPLACE: { labelKey: "users.sourceType.MARKETPLACE", emoji: "🛍️", color: "text-pink-500" },
  ONBOARDING_BONUS: { labelKey: "users.sourceType.ONBOARDING_BONUS", emoji: "🎁", color: "text-teal-500" },
  ORGANIZATION_BONUS: { labelKey: "users.sourceType.ORGANIZATION_BONUS", emoji: "🏢", color: "text-indigo-500" },
  FIRST_PRIZE_BONUS: { labelKey: "users.sourceType.FIRST_PRIZE_BONUS", emoji: "🥇", color: "text-amber-500" },
  CHALLENGE_BUY_IN: { labelKey: "users.sourceType.CHALLENGE_BUY_IN", emoji: "🎫", color: "text-red-400" },
  CHALLENGE_PRIZE: { labelKey: "users.sourceType.CHALLENGE_PRIZE", emoji: "🏆", color: "text-yellow-500" },
  CHALLENGE_REFUND: { labelKey: "users.sourceType.CHALLENGE_REFUND", emoji: "↩️", color: "text-blue-400" },
  CHALLENGE_BURN: { labelKey: "users.sourceType.CHALLENGE_BURN", emoji: "🔥", color: "text-red-600" },
};

// ─── Enum → i18n label-key maps ──────────────────────────────────────────────
// Non-component module: cannot call the t() hook here, so export the i18n KEY
// strings and resolve with t() at the render site. Callers keep a `?? rawValue`
// fallback so an unmapped backend enum degrades to its raw value.

/** Global user role (UserRole) → i18n key. */
export const ROLE_LABEL_KEYS: Record<string, string> = {
  ADMIN: "roles.ADMIN",
  MODERATOR: "roles.MODERATOR",
  ORG_ADMIN: "roles.ORG_ADMIN",
  USER: "roles.USER",
};

/** Subscription tier (UserTier) → i18n key. */
export const TIER_LABEL_KEYS: Record<string, string> = {
  FREE: "tiers.FREE",
  PREMIUM: "tiers.PREMIUM",
  ENTERPRISE: "tiers.ENTERPRISE",
};

/** Account status → i18n key. */
export const ACCOUNT_STATUS_LABEL_KEYS: Record<string, string> = {
  ACTIVE: "accountStatus.ACTIVE",
  PROVISIONED: "accountStatus.PROVISIONED",
  DEACTIVATED: "accountStatus.DEACTIVATED",
};

/** Organization membership role (OrganizationRole) → i18n key. */
export const ORG_ROLE_LABEL_KEYS: Record<string, string> = {
  ADMIN: "users.orgRole.ADMIN",
  MEMBER: "users.orgRole.MEMBER",
};

export const FILTERABLE_SOURCE_TYPES: KoinsSourceType[] = [
  "PHYSICAL_ACTIVITY",
  "TELEMETRY_STEPS",
  "MARKETPLACE",
  "ADJUSTMENT",
  "CHALLENGE_BUY_IN",
  "CHALLENGE_PRIZE",
  "CHALLENGE_REFUND",
  "ONBOARDING_BONUS",
  "ORGANIZATION_BONUS",
  "STREAK_7_DAY",
  "EVENT_ATTEND",
];
